import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSite } from "@/context/SiteContext";
import { PageHero } from "@/components/Shared";
import { IMAGES } from "@/lib/images";
import { ChevronLeft, ChevronRight, X, MessageCircle } from "lucide-react";
import ShareButton from "@/components/ShareButton";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const WEEK = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const pad = (n) => String(n).padStart(2, "0");

const WEEKDAY_NAMES = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];

export default function CalendarPage() {
  const { specialDates, events, closures, settings } = useSite();
  const closedByDate = Object.fromEntries(closures.map((c) => [c.date, c]));
  const today = new Date();
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [selected, setSelected] = useState(null);

  const monthKey = `${cursor.y}-${pad(cursor.m + 1)}`;
  const monthEvents = specialDates.filter((s) => s.date.startsWith(monthKey));
  const eventsByDay = monthEvents.reduce((acc, e) => {
    const d = e.date.slice(8, 10);
    (acc[d] = acc[d] || []).push(e);
    return acc;
  }, {});

  const startOffset = (new Date(cursor.y, cursor.m, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const prevMonth = () => setCursor((c) => (c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 }));
  const nextMonth = () => setCursor((c) => (c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 }));

  const wa = settings.whatsapp || "393331512490";
  const dateKey = (d) => `${cursor.y}-${pad(cursor.m + 1)}-${pad(d)}`;
  const weeklyFor = (d) => events.filter((e) => e.day === WEEKDAY_NAMES[new Date(cursor.y, cursor.m, d).getDay()]);
  const itemsFor = (d) => [
    ...(eventsByDay[pad(d)] || []).map((e) => ({ ...e, image_url: e.image_url || weeklyFor(d)[0]?.image_url || "" })),
    ...weeklyFor(d).map((e) => ({ ...e, id: `w-${e.id}-${d}`, description: e.info, date: dateKey(d), weekly: true })),
  ];
  const openDay = (d) => { if (closedByDate[dateKey(d)]) return; const items = itemsFor(d); if (items.length) setSelected(items); };

  return (
    <div>
      <PageHero overline="Il Mese di Afrodite" title="Calendario Eventi"
        subtitle="Scopri le date speciali e le serate a tema del mese." image={IMAGES.club_bar} />

      <section className="max-w-4xl mx-auto px-3 sm:px-6 py-12 sm:py-16">
        <div className="flex items-center justify-between mb-8 gap-2" data-testid="cal-nav">
          <button onClick={prevMonth} data-testid="cal-prev" aria-label="Mese precedente" className="flex items-center gap-1 pl-2 pr-3 py-2.5 sm:p-3 rounded-full border border-[#D4AF37]/25 text-[#D4AF37] hover:bg-[#D4AF37]/10 active:bg-[#D4AF37]/20 transition-colors text-xs"><ChevronLeft className="w-5 h-5" /><span className="hidden sm:inline">{MONTHS[(cursor.m + 11) % 12].slice(0, 3)}</span></button>
          <div className="flex flex-col items-center gap-1.5">
            <h2 key={monthKey} className="font-display text-xl sm:text-3xl gold-text animate-fade-in" data-testid="cal-month">{MONTHS[cursor.m]} {cursor.y}</h2>
            <div className="flex items-center gap-2">
              <select value={cursor.m} onChange={(e) => setCursor((c) => ({ ...c, m: +e.target.value }))} data-testid="cal-month-select"
                className="bg-[#141217] border border-[#D4AF37]/20 text-[#D8D2CE] text-xs rounded-full px-3 py-1 outline-none">
                {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
              </select>
              <select value={cursor.y} onChange={(e) => setCursor((c) => ({ ...c, y: +e.target.value }))} data-testid="cal-year-select"
                className="bg-[#141217] border border-[#D4AF37]/20 text-[#D8D2CE] text-xs rounded-full px-3 py-1 outline-none">
                {[today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1, today.getFullYear() + 2].map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              {(cursor.m !== today.getMonth() || cursor.y !== today.getFullYear()) && (
                <button onClick={() => setCursor({ y: today.getFullYear(), m: today.getMonth() })} data-testid="cal-today-btn" className="text-xs text-[#D4AF37] underline underline-offset-2">Oggi</button>
              )}
            </div>
          </div>
          <button onClick={nextMonth} data-testid="cal-next" aria-label="Mese successivo" className="flex items-center gap-1 pl-3 pr-2 py-2.5 sm:p-3 rounded-full border border-[#D4AF37]/25 text-[#D4AF37] hover:bg-[#D4AF37]/10 active:bg-[#D4AF37]/20 transition-colors text-xs"><span className="hidden sm:inline">{MONTHS[(cursor.m + 1) % 12].slice(0, 3)}</span><ChevronRight className="w-5 h-5" /></button>
        </div>

        <div className="flex flex-wrap justify-center gap-5 mb-6 text-xs text-[#A39E9B]">
          <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#D4AF37]" /> Club Privé · Mer / Ven / Sab</span>
          <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#C59B6D]" /> Spa Naturista · Mar / Gio / Dom</span>
          <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#941B30] ring-1 ring-[#D4AF37]" /> Data speciale</span>
          <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full border border-[#6E6865] line-through" /> <span className="line-through">Chiuso</span></span>
        </div>

        <div className="grid grid-cols-7 gap-1.5 mb-2">
          {WEEK.map((w) => <div key={w} className="text-center text-xs text-[#6E6865] uppercase tracking-widest py-2">{w}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
          {cells.map((d, i) => {
            if (!d) return <div key={i} />;
            const key = pad(d);
            const evs = eventsByDay[key];
            const weekly = weeklyFor(d);
            const cat = weekly[0]?.category;
            const open = evs || weekly.length > 0;
            const closed = closedByDate[dateKey(d)];
            const isToday = cursor.y === today.getFullYear() && cursor.m === today.getMonth() && d === today.getDate();
            if (closed) {
              return (
                <div key={i} data-testid={`cal-day-${d}`} data-category="closed" title={closed.reason || "Chiuso"}
                  className={`aspect-square rounded-lg flex flex-col items-center justify-center relative border border-dashed border-[#6E6865]/50 bg-[#0B0A0C] text-[#6E6865] ${isToday ? "ring-1 ring-[#D4AF37]" : ""}`}>
                  <span className="line-through decoration-[#941B30] decoration-2">{d}</span>
                  <span className="hidden sm:block text-[0.5rem] uppercase tracking-widest mt-0.5 text-[#941B30]">Chiuso</span>
                </div>
              );
            }
            const tone = evs ? "bg-[#7A1426]/40 border border-[#D4AF37]/60 text-[#FBF8F5] hover:bg-[#7A1426]/60"
              : cat === "spa" ? "bg-[#C59B6D]/10 border border-[#C59B6D]/35 text-[#FBF8F5] hover:bg-[#C59B6D]/25"
              : cat === "club" ? "bg-[#7A1426]/15 border border-[#D4AF37]/30 text-[#FBF8F5] hover:bg-[#7A1426]/35"
              : "border border-white/5 text-[#6E6865] cursor-default";
            return (
              <button key={i} onClick={() => openDay(d)} disabled={!open} data-testid={`cal-day-${d}`}
                data-category={evs ? "special" : cat || ""}
                className={`aspect-square rounded-lg flex flex-col items-center justify-center relative transition-all duration-300 text-sm ${tone} ${open ? "cursor-pointer" : ""}
                  ${isToday ? "ring-1 ring-[#D4AF37]" : ""}`}>
                <span>{d}</span>
                {cat && !evs && <span className="hidden sm:block text-[0.5rem] uppercase tracking-widest mt-0.5" style={{ color: cat === "spa" ? "#C59B6D" : "#D4AF37" }}>{cat === "spa" ? "Spa" : "Club"}</span>}
                {evs && <span className="absolute bottom-1.5 w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />}
                {cat && !evs && <span className="absolute bottom-1.5 w-1.5 h-1.5 rounded-full" style={{ background: cat === "spa" ? "#C59B6D" : "#D4AF37" }} />}
              </button>
            );
          })}
        </div>

        {monthEvents.length === 0 && <p className="text-center text-[#6E6865] mt-12">Nessun evento speciale in programma questo mese.</p>}

        {monthEvents.length > 0 && (
          <div className="mt-12 space-y-3">
            <p className="overline mb-4">In Programma</p>
            {monthEvents.map((e) => (
              <button key={e.id} data-testid={`cal-event-${e.id}`} onClick={() => setSelected(eventsByDay[e.date.slice(8, 10)])}
                className="w-full flex items-center gap-4 p-4 rounded-xl border border-[#D4AF37]/12 bg-[#141217] text-left hover:border-[#D4AF37]/35 transition-colors">
                <div className="w-12 text-center shrink-0">
                  <div className="font-display text-2xl text-[#D4AF37]">{e.date.slice(8, 10)}</div>
                  <div className="text-[0.6rem] uppercase text-[#6E6865]">{MONTHS[cursor.m].slice(0, 3)}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg text-[#FBF8F5] truncate">{e.title}</p>
                  <p className="text-xs text-[#A39E9B] truncate">{e.description}</p>
                </div>
                <span className={`text-[0.6rem] px-2 py-0.5 rounded-full shrink-0 ${e.category === "spa" ? "bg-[#C59B6D]/20 text-[#C59B6D]" : "bg-[#7A1426]/30 text-[#F3E5AB]"}`}>{e.category === "spa" ? "Spa" : "Club"}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      <AnimatePresence>
        {selected && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setSelected(null)} data-testid="cal-detail-modal"
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/95" style={{ backdropFilter: "blur(18px)" }}>
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-lg glass border border-[#D4AF37]/25 rounded-2xl overflow-hidden max-h-[88vh] overflow-y-auto">
              <button onClick={() => setSelected(null)} data-testid="cal-detail-close"
                className="absolute top-4 right-4 p-2 rounded-full bg-black/50 text-[#FBF8F5] hover:text-[#D4AF37] z-10 transition-colors"><X className="w-5 h-5" /></button>
              {selected.map((e) => (
                <div key={e.id} className="border-b border-white/5 last:border-0">
                  {e.image_url && (
                    <div className="relative h-56 bg-black">
                      <img src={e.image_url} alt={e.title} className="w-full h-full object-cover object-top" />
                      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,transparent,rgba(20,18,23,0.95))" }} />
                    </div>
                  )}
                  <div className="p-6">
                    <p className="text-xs text-[#D4AF37] mb-1 capitalize">{new Date(e.date + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" })}{e.weekly && <span className="ml-2 text-[#6E6865] normal-case">· {e.category === "spa" ? "Pomeriggio Spa" : "Serata Club"} settimanale</span>}</p>
                    <h3 className="font-display text-2xl text-[#FBF8F5] mb-2">{e.title}</h3>
                    <p className="text-sm text-[#A39E9B] leading-relaxed mb-4">{e.description}</p>
                    <div className="flex flex-wrap items-center gap-3">
                      <a href={`https://wa.me/${wa}?text=${encodeURIComponent("Salve, vorrei informazioni sull'evento: " + e.title)}`}
                        target="_blank" rel="noopener noreferrer" data-testid={`cal-book-${e.id}`}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#25D366] text-black text-sm font-semibold hover:scale-105 transition-transform">
                        <MessageCircle className="w-4 h-4" /> Prenota su WhatsApp
                      </a>
                      <ShareButton testid={`cal-share-${e.id}`} title={e.title} image={e.image_url}
                        when={new Date(e.date + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })} />
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
